import React, { useState, useMemo } from 'react';
import { Card, Button, Table, Select, Upload, message, Modal, Typography, Tooltip } from 'antd';
import { DownloadOutlined, UploadOutlined, ReloadOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useLazyQuery } from '@apollo/client';
import type { ColumnsType } from 'antd/es/table';
import * as XLSX from 'xlsx';
import { t } from '../../../i18n';
import AsyncReportDownloadModal, { dashboardAsyncReportLabelI18nKeys } from '../../../components/asyncReportDownload';
import { hasResource } from '../../../utils/resourceAuth';
import { ResourceCode } from '../../../constants/resourceCodes';
import {
  GET_QC_PENALTY_UPLOAD_TASKS,
  GET_QC_PENALTY_TASK_RELATED_BILL_IDS,
} from './graphql/queries';
import {
  SUBMIT_QC_PENALTY_UPLOAD,
  SUBMIT_QC_PENALTY_UPLOAD_RESULT_TO_DOWNLOAD_CENTER,
} from './graphql/mutations';
import type {
  QcPenaltyUploadTaskType,
  GetQcPenaltyUploadTasksResponse,
  SubmitQcPenaltyUploadResultToDownloadCenterResponse,
  GetQcPenaltyTaskRelatedBillIdsResponse,
} from './graphql/types';

const TEMPLATE_NAME = 'QC_Penalty_Template.xlsx';
const TEMPLATE_HEADERS = ['Tracking Number', 'Penalty Type', 'Penalty Amount', 'Penalty DSP NAME', 'Penalty Driver Name'];
const PENALTY_TYPES = [
  'Customer Claim-DNR',
  'Severely Violated POD',
  'General Non-Compliant POD',
  'Malicious Delivery Behavior',
  'Public Sentiment',
  'Stale Package',
];
const TASK_STATUS_UPLOADED = 'UPLOADED';
const TASK_STATUS_PARSING = 'PARSING';
const TASK_STATUS_PENDING_APPROVAL = 'PENDING_APPROVAL';
const TASK_STATUS_APPROVAL_REJECTED = 'APPROVAL_REJECTED';
const TASK_STATUS_VALIDATION_FAILED = 'VALIDATION_FAILED';
const TASK_STATUS_EXECUTING = 'EXECUTING';
const TASK_STATUS_COMPLETED = 'COMPLETED';

const IN_PROGRESS_STATUSES = new Set([
  TASK_STATUS_UPLOADED,
  TASK_STATUS_PARSING,
  TASK_STATUS_PENDING_APPROVAL,
  TASK_STATUS_EXECUTING,
]);

// 账期边界与列表展示统一使用 UTC，避免本地时区差导致 label 与列表显示不一致。
// 用整秒 23:59:59（不带 .999 毫秒）：MySQL TIMESTAMP 默认按四舍五入入库，
// 23:59:59.999 会被进位到次日 00:00:00，导致账期多算一天。
const fmtUtcDate = (d: Date) =>
  `${d.getUTCFullYear()}/${String(d.getUTCMonth() + 1).padStart(2, '0')}/${String(d.getUTCDate()).padStart(2, '0')}`;

/**
 * 计算上一周和上上周的账期（周一 00:00:00 UTC ~ 周日 23:59:59 UTC）
 */
function getWeekPeriods(): { label: string; periodStart: string; periodEnd: string }[] {
  const now = new Date();
  const dayOfWeek = now.getUTCDay(); // 0=Sun, 1=Mon, ...
  const diffToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
  const oneWeekMs = 7 * 24 * 60 * 60 * 1000;

  // 本周一 00:00:00 UTC
  const thisMonday = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + diffToMonday));
  // 本周日 23:59:59 UTC（不带毫秒，避免 MySQL 进位）
  const thisSunday = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + diffToMonday + 6, 23, 59, 59, 0));

  // 上周
  const lastMonday = new Date(thisMonday.getTime() - oneWeekMs);
  const lastSunday = new Date(thisSunday.getTime() - oneWeekMs);

  // 上上周
  const prevMonday = new Date(thisMonday.getTime() - 2 * oneWeekMs);
  const prevSunday = new Date(thisSunday.getTime() - 2 * oneWeekMs);

  return [
    {
      label: `${fmtUtcDate(lastMonday)} ~ ${fmtUtcDate(lastSunday)} (Last Week)`,
      periodStart: lastMonday.toISOString(),
      periodEnd: lastSunday.toISOString(),
    },
    {
      label: `${fmtUtcDate(prevMonday)} ~ ${fmtUtcDate(prevSunday)} (Week Before Last)`,
      periodStart: prevMonday.toISOString(),
      periodEnd: prevSunday.toISOString(),
    },
  ];
}

const readFileAsBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const dataUrl = reader.result;
      if (typeof dataUrl !== 'string') {
        reject(new Error('Failed to read file'));
        return;
      }
      const comma = dataUrl.indexOf(',');
      if (comma === -1) {
        reject(new Error('Failed to read file'));
        return;
      }
      resolve(dataUrl.slice(comma + 1));
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });

const QcPenaltyUploadPage: React.FC = () => {
  const [selectedPeriodIdx, setSelectedPeriodIdx] = useState<number | undefined>(undefined);
  const [uploading, setUploading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const [downloadModalVisible, setDownloadModalVisible] = useState(false);
  const [downloadModalFileName, setDownloadModalFileName] = useState('');
  const [billIdsModalVisible, setBillIdsModalVisible] = useState(false);
  const [billIdsModalTaskId, setBillIdsModalTaskId] = useState<string | null>(null);

  const weekPeriods = useMemo(() => getWeekPeriods(), []);

  const { data: tasksData, loading: tasksLoading, refetch: refetchTasks } = useQuery<GetQcPenaltyUploadTasksResponse>(
    GET_QC_PENALTY_UPLOAD_TASKS,
    { fetchPolicy: 'cache-and-network' }
  );

  const [submitUpload] = useMutation(SUBMIT_QC_PENALTY_UPLOAD);
  const [submitResultToDownloadCenter] = useMutation<SubmitQcPenaltyUploadResultToDownloadCenterResponse>(
    SUBMIT_QC_PENALTY_UPLOAD_RESULT_TO_DOWNLOAD_CENTER
  );

  const [
    fetchRelatedBillIds,
    { data: billIdsData, loading: billIdsLoading },
  ] = useLazyQuery<GetQcPenaltyTaskRelatedBillIdsResponse>(
    GET_QC_PENALTY_TASK_RELATED_BILL_IDS,
    { fetchPolicy: 'network-only' }
  );

  const handleViewRelatedBillIds = (taskId: string) => {
    setBillIdsModalTaskId(taskId);
    setBillIdsModalVisible(true);
    fetchRelatedBillIds({ variables: { taskId: Number(taskId) } });
  };

  const billIdsModalContent = useMemo(() => {
    const ids = billIdsData?.getQcPenaltyTaskRelatedBillIds ?? [];
    return ids.length > 0 ? ids.join('\n') : '';
  }, [billIdsData]);

  const handleDownloadTemplate = () => {
    const worksheetData = [
      TEMPLATE_HEADERS,
      ...PENALTY_TYPES.map((type) => ['', type, '', '', '']),
    ];
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', TEMPLATE_NAME);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    message.success(t('qualityControl.penaltyUpload.templateDownloaded'));
  };

  const handleUpload = async (file: File) => {
    const isExcel = file.name.endsWith('.xlsx') || file.name.endsWith('.xls');
    if (!isExcel) {
      message.error(t('qualityControl.penaltyUpload.uploadExcelOnly'));
      return false;
    }
    if (selectedPeriodIdx === undefined) {
      message.error(t('qualityControl.penaltyUpload.selectBillingPeriodFirst'));
      return false;
    }

    const period = weekPeriods[selectedPeriodIdx];
    setUploading(true);
    message.loading({
      content: t('qualityControl.penaltyUpload.uploading'),
      key: 'qc-penalty-upload',
      duration: 0,
    });

    try {
      // 读取文件为 Base64
      const fileBase64 = await readFileAsBase64(file);
      const result = await submitUpload({
        variables: {
          periodStart: period.periodStart,
          periodEnd: period.periodEnd,
          fileBase64,
          fileName: file.name,
        },
      });

      message.destroy('qc-penalty-upload');
      const taskId = result.data?.submitQcPenaltyUpload;
      message.success(t('qualityControl.penaltyUpload.taskSubmitted', { taskId }));
      refetchTasks();
    } catch (error: unknown) {
      message.destroy('qc-penalty-upload');
      const err = error as Error;
      message.error(t('qualityControl.penaltyUpload.uploadFailed', { error: err?.message || 'Unknown error' }));
    } finally {
      setUploading(false);
    }
    return false;
  };

  const handleDownloadResult = async (record: QcPenaltyUploadTaskType) => {
    if (
      record.status !== TASK_STATUS_COMPLETED &&
      record.status !== TASK_STATUS_VALIDATION_FAILED &&
      record.status !== TASK_STATUS_APPROVAL_REJECTED
    ) {
      message.warning(t('qualityControl.penaltyUpload.resultNotReady'));
      return;
    }
    try {
      const res = await submitResultToDownloadCenter({ variables: { taskId: Number(record.id) } });
      const success = res.data?.submitQcPenaltyUploadResultToDownloadCenter;
      if (success) {
        setDownloadModalFileName(
          record.resultFileName || record.sourceFileName || `qc_penalty_result_${record.id}.xlsx`
        );
        setDownloadModalVisible(true);
      } else {
        message.warning(t('qualityControl.penaltyUpload.resultNotAvailable'));
      }
    } catch {
      message.warning(t('qualityControl.penaltyUpload.submitDownloadCenterFailed'));
    }
  };

  const tasks = tasksData?.getQcPenaltyUploadTasks ?? [];
  const hasInProgressTask = tasks.some((task) => IN_PROGRESS_STATUSES.has(task.status));

  const formatPeriod = (record: QcPenaltyUploadTaskType) => {
    const start = record.periodStart ? fmtUtcDate(new Date(record.periodStart)) : '?';
    const end = record.periodEnd ? fmtUtcDate(new Date(record.periodEnd)) : '?';
    return `${start} ~ ${end}`;
  };

  const formatProgress = (record: QcPenaltyUploadTaskType) => {
    switch (record.status) {
      case TASK_STATUS_UPLOADED:
        return t('qualityControl.penaltyUpload.progress.uploaded');
      case TASK_STATUS_PARSING:
        return t('qualityControl.penaltyUpload.progress.parsing');
      case TASK_STATUS_PENDING_APPROVAL:
        return t('qualityControl.penaltyUpload.progress.pendingApproval');
      case TASK_STATUS_APPROVAL_REJECTED:
        return t('qualityControl.penaltyUpload.progress.approvalRejected');
      case TASK_STATUS_VALIDATION_FAILED:
        return record.failureReason || t('qualityControl.penaltyUpload.progress.validationFailed');
      case TASK_STATUS_EXECUTING:
        return t('qualityControl.penaltyUpload.progress.executing');
      case TASK_STATUS_COMPLETED:
        return t('qualityControl.penaltyUpload.progress.completed', {
          success: record.successCount,
          fail: record.failCount,
        });
      default:
        return record.status;
    }
  };

  const canDownloadResult = (record: QcPenaltyUploadTaskType) =>
    record.status === TASK_STATUS_COMPLETED ||
    record.status === TASK_STATUS_VALIDATION_FAILED ||
    record.status === TASK_STATUS_APPROVAL_REJECTED;

  const columns: ColumnsType<QcPenaltyUploadTaskType> = [
    { title: t('qualityControl.penaltyUpload.column.taskId'), dataIndex: 'id', key: 'id', width: 100 },
    {
      title: t('qualityControl.penaltyUpload.column.fileName'),
      dataIndex: 'sourceFileName',
      key: 'sourceFileName',
      width: 200,
      ellipsis: true,
      render: (v: string | null | undefined) => v ?? '—',
    },
    {
      title: t('qualityControl.penaltyUpload.column.billingPeriod'),
      key: 'period',
      width: 230,
      render: (_, record) => (
        <span className="inline-flex items-center gap-1">
          {formatPeriod(record)}
          <Tooltip title={t('qualityControl.penaltyUpload.column.viewBillIds')}>
            <Button
              type="text"
              size="small"
              icon={<QuestionCircleOutlined />}
              onClick={() => handleViewRelatedBillIds(record.id)}
            />
          </Tooltip>
        </span>
      ),
    },
    {
      title: t('qualityControl.penaltyUpload.column.submitter'),
      dataIndex: 'submitterName',
      key: 'submitterName',
      width: 120,
    },
    {
      title: t('qualityControl.penaltyUpload.column.progress'),
      key: 'progress',
      width: 250,
      render: (_, record) => {
        const text = formatProgress(record);
        const color = record.status === TASK_STATUS_VALIDATION_FAILED || record.status === TASK_STATUS_APPROVAL_REJECTED
          ? 'text-red-600'
          : record.status === TASK_STATUS_COMPLETED ? 'text-green-600'
          : record.status === TASK_STATUS_PENDING_APPROVAL ? 'text-orange-600'
          : '';
        return <span className={color}>{text}</span>;
      },
    },
    {
      title: t('qualityControl.penaltyUpload.column.createdAt'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 180,
      render: (v: string | null | undefined) => (v ? new Date(v).toLocaleString() : '—'),
    },
    {
      title: t('qualityControl.penaltyUpload.column.result'),
      key: 'download',
      width: 120,
      render: (_, record) => {
        if (!canDownloadResult(record)) return null;
        return (
          <Button
            type="link"
            size="small"
            icon={<DownloadOutlined />}
            onClick={() => handleDownloadResult(record)}
          >
            {t('qualityControl.penaltyUpload.column.download')}
          </Button>
        );
      },
    },
  ];

  return (
    <div className="flex flex-col gap-6">
      <div className="text-2xl font-bold">{t('qualityControl.penaltyUpload.title')}</div>

      <Card title={t('qualityControl.penaltyUpload.uploadSettings')}>
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="whitespace-nowrap font-medium">{t('qualityControl.penaltyUpload.billingPeriod')}:</span>
              <Select
                value={selectedPeriodIdx}
                onChange={(v) => setSelectedPeriodIdx(v)}
                placeholder={t('qualityControl.penaltyUpload.selectBillingPeriod')}
                allowClear
                className="w-80"
              >
                {weekPeriods.map((p, idx) => (
                  <Select.Option key={idx} value={idx}>
                    {p.label}
                  </Select.Option>
                ))}
              </Select>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button type="primary" icon={<DownloadOutlined />} onClick={handleDownloadTemplate}>
              {t('qualityControl.penaltyUpload.downloadTemplate')}
            </Button>
            <Upload
              accept=".xlsx,.xls"
              showUploadList={false}
              beforeUpload={(file) => {
                handleUpload(file);
                return false;
              }}
              disabled={uploading || selectedPeriodIdx === undefined || hasInProgressTask}
            >
              <Button
                icon={<UploadOutlined />}
                loading={uploading}
                disabled={selectedPeriodIdx === undefined || hasInProgressTask}
              >
                {t('qualityControl.penaltyUpload.uploadPenaltyData')}
              </Button>
            </Upload>
          </div>
          {hasInProgressTask && (
            <Typography.Text type="warning">
              {t('qualityControl.penaltyUpload.inProgressBlocked')}
            </Typography.Text>
          )}
        </div>
      </Card>

      <Card
        title={t('qualityControl.penaltyUpload.uploadTasks')}
        extra={
          <Button
            icon={<ReloadOutlined />}
            onClick={() => refetchTasks()}
            loading={tasksLoading}
          >
            {t('qualityControl.penaltyUpload.refresh')}
          </Button>
        }
      >
        <Table
          rowKey="id"
          columns={columns}
          dataSource={tasks}
          loading={tasksLoading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            showSizeChanger: true,
            onChange: (current, pageSize) => {
              setPagination({ current, pageSize });
            },
            onShowSizeChange: (current, size) => {
              setPagination({ current, pageSize: size });
            },
            showTotal: (total) => t('qualityControl.penaltyUpload.totalTasks', { total }),
          }}
          scroll={{ x: 1200 }}
          locale={{ emptyText: t('qualityControl.penaltyUpload.noTasks') }}
        />
      </Card>
      <AsyncReportDownloadModal
        visible={downloadModalVisible}
        onClose={() => setDownloadModalVisible(false)}
        fileName={downloadModalFileName}
        reportLabelI18nKey={dashboardAsyncReportLabelI18nKeys.qcPenaltyUpload}
        showGoToDownloadPage={hasResource([ResourceCode.cs.CS_DOWNLOAD])}
      />
      <Modal
        open={billIdsModalVisible}
        onCancel={() => setBillIdsModalVisible(false)}
        title={t('qualityControl.penaltyUpload.relatedBillIds.title', { taskId: billIdsModalTaskId ?? '' })}
        footer={null}
        width={480}
      >
        {billIdsLoading ? (
          <div className="py-4 text-center text-gray-500">{t('qualityControl.penaltyUpload.relatedBillIds.loading')}</div>
        ) : billIdsModalContent ? (
          <Typography.Paragraph
            copyable={{ text: billIdsModalContent }}
            style={{ whiteSpace: 'pre-wrap', marginBottom: 0 }}
          >
            {billIdsModalContent}
          </Typography.Paragraph>
        ) : (
          <div className="py-4 text-center text-gray-500">{t('qualityControl.penaltyUpload.relatedBillIds.empty')}</div>
        )}
      </Modal>
    </div>
  );
};

export default QcPenaltyUploadPage;
